// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppDataProvider } from "./context/AppDataContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { NotificationProvider } from "./context/NotificationContext";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "./i18n";

// Import all components and pages
import PrivateRoute from "./components/PrivateRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import Layout from "./components/Layout";
import ApprovalModal from "./components/ApprovalModal";
import FloatingAssistant from "./components/FloatingAssistant";
import AdminPage from "./pages/AdminPage";
import Loader from "./components/Loader";
import HomePage from "./pages/HomePage";
import AuthPage from "./pages/AuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ChatPage from "./pages/ChatPage";
import ProfilePage from "./pages/ProfilePage";
import ProductFormPage from "./pages/ProductFormPage";
import EditProfilePage from "./pages/EditProfilePage";
import SearchResultsPage from "./pages/SearchResultsPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import FlashDealsPage from "./pages/FlashDealsPage";
import NewArrivalsPage from "./pages/NewArrivalsPage";
import FavoritesPage from "./pages/FavoritesPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OTPPage from "./pages/OTPPage";
import OnboardingPage from "./pages/OnboardingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import AboutPage from "./pages/AboutPage";

import "./styles/theme.css";

function AppRoutes() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { ready: i18nReady } = useTranslation();

  if (authLoading || !i18nReady) {
    return <Loader />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" /> : <AuthPage />}
      />
      <Route
        path="/admin-login"
        element={isAuthenticated ? <Navigate to="/admin" /> : <AdminLoginPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/verify-otp" element={<OTPPage />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="search/:searchTerm?" element={<SearchResultsPage />} />
        <Route
          path="category/:categoryId/:categoryName"
          element={<SearchResultsPage />}
        />
        <Route path="users/:id" element={<PublicProfilePage />} />
        <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="terms-of-service" element={<TermsPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="flash-deals" element={<FlashDealsPage />} />
        <Route path="new-arrivals" element={<NewArrivalsPage />} />
          <Route element={<PrivateRoute />}>
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="chat/:conversationId" element={<ChatPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="profile/edit" element={<EditProfilePage />} />
            <Route path="favorites" element={<FavoritesPage />} />
            <Route path="cart" element={<CartPage />} />
            <Route path="checkout" element={<CheckoutPage />} />
            <Route path="products/add" element={<ProductFormPage />} />
            <Route path="products/:id/edit" element={<ProductFormPage />} />
          </Route>
        <Route element={<AdminProtectedRoute />}>
          <Route path="admin" element={<AdminPage />} />
        </Route>
      </Route>
      <Route path="*" element={<h1>404: Page Not Found</h1>} />
    </Routes>
  );
}

function GlobalOverlays() {
  const location = useLocation();
  const isChatPage = location.pathname.startsWith("/chat");
  if (isChatPage) return null;
  return (
    <>
      <ApprovalModal />
      <FloatingAssistant />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <AppDataProvider>
            <CurrencyProvider>
              <NotificationProvider>
                <I18nextProvider i18n={i18n}>
                  <div className="app-container">
                    <AppRoutes />
                    <GlobalOverlays />
                  </div>
                </I18nextProvider>
              </NotificationProvider>
            </CurrencyProvider>
          </AppDataProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}
export default App;
